import { Building, Mail, MapPin, Phone, User } from 'lucide-react';
import { formatDate } from '@/utils/formatters';
import { InvestorEntity } from '../services/investor-service';
import { formatContactRole } from '../utils/entity-formatters';

interface ContactDisplayProps {
  contact: {
    name: string;
    email: string;
    phone: string;
    role: string;
    title: string;
  };
}

const ContactDisplay = ({ contact }: ContactDisplayProps) => (
  <div className="text-sm text-muted-foreground space-y-1">
    <p className="font-medium text-foreground">{contact.name}</p>
    <p className="text-xs text-muted-foreground">
      {contact.title
        ? `${contact.title} • ${formatContactRole(contact.role)}`
        : formatContactRole(contact.role)}
    </p>
    <p className="flex items-center">
      <Mail className="h-3 w-3 mr-1" />
      {contact.email}
    </p>
    <p className="flex items-center">
      <Phone className="h-3 w-3 mr-1" />
      {contact.phone}
    </p>
  </div>
);

interface EntityTopRowProps {
  entity: InvestorEntity;
}

export function EntityTopRow({ entity }: EntityTopRowProps) {
  const hasEntityDetails =
    entity.type === 'company' &&
    Boolean(
      entity.taxId || entity.registrationNumber || entity.incorporationDate
    );

  const { line1, line2, city, postcode, country } = entity.address;
  const hasAddress = Boolean(line1 || line2 || city || postcode || country);

  const primaryContact = entity.contacts.primary;
  const hasPrimaryContact = Boolean(
    primaryContact.name || primaryContact.email || primaryContact.phone
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {/* Address */}
      {hasAddress && (
        <div>
          <h4 className="font-medium mb-2 flex items-center">
            <MapPin className="h-4 w-4 mr-1" />
            Address
          </h4>
          <div className="text-sm text-muted-foreground space-y-1">
            {line1 && <p>{line1}</p>}
            {line2 && <p>{line2}</p>}
            {(city || postcode) && (
              <p>
                {city}
                {city && postcode ? ', ' : ''}
                {postcode}
              </p>
            )}
            {country && <p>{country}</p>}
          </div>
        </div>
      )}

      {/* Contacts */}
      {hasPrimaryContact && (
        <div>
          <h4 className="font-medium mb-2 flex items-center">
            <User className="h-4 w-4 mr-1" />
            Primary Contact
          </h4>
          <ContactDisplay contact={primaryContact} />
        </div>
      )}

      {/* Entity Details */}
      {hasEntityDetails && (
        <div>
          <h4 className="font-medium mb-2 flex items-center">
            <Building className="h-4 w-4 mr-1" />
            Entity Details
          </h4>
          <div className="text-sm text-muted-foreground space-y-1">
            {entity.taxId && (
              <p>
                <span className="text-muted-foreground">Tax ID:</span>
                <span className="ml-2">{entity.taxId}</span>
              </p>
            )}
            {entity.registrationNumber && (
              <p>
                <span className="text-muted-foreground">Registration:</span>
                <span className="ml-2">{entity.registrationNumber}</span>
              </p>
            )}
            {entity.incorporationDate && (
              <p>
                <span className="text-muted-foreground">Incorporated:</span>
                <span className="ml-2">
                  {formatDate(entity.incorporationDate)}
                </span>
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
